import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  Save,
  History,
  RotateCcw,
  Plus,
  Trash2,
  FileText,
  X,
} from 'lucide-react';
import Swal from 'sweetalert2';
import { PageHeader } from '../../components/PageHeader';
import {
  editorialAiService,
  AiSubagent,
  PromptConfig,
  PromptVersion,
} from '../../services/editorial-ai.service';

/**
 * AI prompt management (NWFE-RQ-01.4, admin-only).
 * Layered configuration (client decision):
 *   base per stage → overrides per question type → optional per-OE overrides.
 * Every save creates a new version; rollback is a single action; history
 * shows author, timestamp and change notes.
 */

const SUBAGENT_LABELS: Record<AiSubagent, string> = {
  linguistic: 'Linguistic (Stage 1 — grammar)',
  pedagogical: 'Pedagogical (Stage 2 — conceptual)',
};

const QUESTION_TYPES = ['multiple_choice', 'short_answer', 'case_study', 'interactive'];

export function PromptManagement() {
  const queryClient = useQueryClient();
  const [subagent, setSubagent] = useState<AiSubagent>('linguistic');
  const [baseContent, setBaseContent] = useState('');
  const [baseNotes, setBaseNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [historyFor, setHistoryFor] = useState<PromptConfig | null>(null);
  const [versions, setVersions] = useState<PromptVersion[]>([]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['ai-prompts'],
    queryFn: () => editorialAiService.listPrompts(),
  });

  const configs = data?.configs || [];
  const baseConfig = configs.find(
    (c) => c.subagent === subagent && c.level === 'stage' && c.isActive
  );
  const overrides = configs.filter(
    (c) => c.subagent === subagent && c.level !== 'stage' && c.isActive
  );

  // Load the base editor whenever the subagent (or data) changes
  useEffect(() => {
    setBaseContent(baseConfig?.activeContent ?? data?.defaults?.[subagent] ?? '');
    setBaseNotes('');
  }, [subagent, baseConfig?.activeContent, data?.defaults]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['ai-prompts'] });

  const saveBase = async () => {
    if (!baseContent.trim()) return;
    setIsSaving(true);
    try {
      await editorialAiService.upsertPrompt({
        subagent,
        level: 'stage',
        content: baseContent,
        notes: baseNotes || undefined,
      });
      invalidate();
      Swal.fire({
        title: 'Saved',
        text: 'A new prompt version was created and activated.',
        icon: 'success',
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err: any) {
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.error || 'Could not save the prompt.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const openHistory = async (config: PromptConfig) => {
    try {
      const list = await editorialAiService.getPromptVersions(config._id);
      setVersions(list);
      setHistoryFor(config);
    } catch (err: any) {
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.error || 'Could not load version history.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    }
  };

  const rollback = async (config: PromptConfig, version: number) => {
    const confirm = await Swal.fire({
      title: `Rollback to v${version}?`,
      text: 'The selected version becomes active immediately. History is kept.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Rollback',
      confirmButtonColor: '#1e73be',
    });
    if (!confirm.isConfirmed) return;

    try {
      await editorialAiService.rollbackPrompt(config._id, version);
      invalidate();
      setHistoryFor(null);
      Swal.fire({
        title: 'Rolled back',
        text: `Version ${version} is now active.`,
        icon: 'success',
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err: any) {
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.error || 'Rollback failed.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    }
  };

  const addOverride = async () => {
    const result = await Swal.fire({
      title: 'New prompt override',
      html: `
        <select id="ovrLevel" class="swal2-select" style="width:80%">
          <option value="question_type">Per question type</option>
          <option value="oe">Per OE (edge cases)</option>
        </select>
        <select id="ovrType" class="swal2-select" style="width:80%">
          ${QUESTION_TYPES.map((t) => `<option value="${t}">${t}</option>`).join('')}
        </select>
        <input id="ovrOe" class="swal2-input" placeholder="OE code (e.g. OE-1.2)" style="display:none">
        <p style="font-size:12px;color:#777;margin-top:4px">Overrides inherit from the base prompt — write only what differs.</p>
      `,
      didOpen: () => {
        const level = document.getElementById('ovrLevel') as HTMLSelectElement;
        const type = document.getElementById('ovrType') as HTMLSelectElement;
        const oe = document.getElementById('ovrOe') as HTMLInputElement;
        level.addEventListener('change', () => {
          const isOe = level.value === 'oe';
          type.style.display = isOe ? 'none' : '';
          oe.style.display = isOe ? '' : 'none';
        });
      },
      showCancelButton: true,
      confirmButtonText: 'Create',
      confirmButtonColor: '#1e73be',
      preConfirm: () => {
        const level = (document.getElementById('ovrLevel') as HTMLSelectElement).value;
        const questionType = (document.getElementById('ovrType') as HTMLSelectElement).value;
        const oeCode = (document.getElementById('ovrOe') as HTMLInputElement).value.trim();
        if (level === 'oe' && !oeCode) {
          Swal.showValidationMessage('OE code is required');
          return false;
        }
        return { level, questionType, oeCode };
      },
    });

    if (!result.isConfirmed || !result.value) return;
    const { level, questionType, oeCode } = result.value as any;

    try {
      await editorialAiService.upsertPrompt({
        subagent,
        level,
        questionType: level === 'question_type' ? questionType : undefined,
        oeCode: level === 'oe' ? oeCode : undefined,
        content: baseConfig?.activeContent || data?.defaults?.[subagent] || '',
        notes: 'Created from base prompt',
      });
      invalidate();
    } catch (err: any) {
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.error || 'Could not create the override.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    }
  };

  const editOverride = async (config: PromptConfig) => {
    const result = await Swal.fire({
      title: `Edit override — ${config.level === 'oe' ? config.oeCode : config.questionType}`,
      html: `
        <textarea id="ovrContent" class="swal2-textarea" style="height:260px;font-family:monospace;font-size:12px">${config.activeContent.replace(/</g, '&lt;')}</textarea>
        <input id="ovrNotes" class="swal2-input" placeholder="Change notes (optional)">
      `,
      width: 720,
      showCancelButton: true,
      confirmButtonText: 'Save new version',
      confirmButtonColor: '#1e73be',
      preConfirm: () => {
        const content = (document.getElementById('ovrContent') as HTMLTextAreaElement).value;
        const notes = (document.getElementById('ovrNotes') as HTMLInputElement).value.trim();
        if (!content.trim()) {
          Swal.showValidationMessage('Prompt content is required');
          return false;
        }
        return { content, notes };
      },
    });

    if (!result.isConfirmed || !result.value) return;
    const { content, notes } = result.value as any;

    try {
      await editorialAiService.upsertPrompt({
        subagent: config.subagent,
        level: config.level,
        questionType: config.questionType || undefined,
        oeCode: config.oeCode || undefined,
        content,
        notes: notes || undefined,
      });
      invalidate();
    } catch (err: any) {
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.error || 'Could not save the override.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    }
  };

  const removeOverride = async (config: PromptConfig) => {
    const confirm = await Swal.fire({
      title: 'Remove override?',
      text: 'Questions will fall back to the base prompt. Version history is kept.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Remove',
      confirmButtonColor: '#dc2626',
    });
    if (!confirm.isConfirmed) return;
    try {
      await editorialAiService.deactivatePrompt(config._id);
      invalidate();
    } catch (err: any) {
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.error || 'Could not remove the override.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="p-10 flex items-center justify-center gap-2 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Loading prompts...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-10 text-center text-sm text-red-500">
        {(error as any)?.response?.status === 403
          ? 'You do not have access to prompt management.'
          : (error as any)?.response?.data?.error || 'Failed to load prompts.'}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="AI Prompt Management"
        description="Prompts for the AI review subagents — versioned, with rollback and layered overrides"
        breadcrumbs={[{ label: 'Editorial Review' }, { label: 'AI Prompts' }]}
      />

      {/* Subagent tabs */}
      <div className="flex gap-2 mt-4 mb-6">
        {(Object.keys(SUBAGENT_LABELS) as AiSubagent[]).map((key) => (
          <button
            key={key}
            onClick={() => setSubagent(key)}
            className={`px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
              subagent === key
                ? 'bg-[#1e73be] text-white border-[#1e73be]'
                : 'bg-white text-gray-600 border-gray-200 hover:border-[#1e73be]'
            }`}
          >
            {SUBAGENT_LABELS[key]}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Base prompt editor */}
        <div className="xl:col-span-2 bg-white rounded-2xl shadow-md border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-400" /> Base prompt (per stage)
              </h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {baseConfig
                  ? `Active: v${baseConfig.activeVersion} · ${baseConfig.activeVersionAuthor || '—'} · ${baseConfig.activeVersionAt ? new Date(baseConfig.activeVersionAt).toLocaleString() : ''}`
                  : 'Using the built-in default — saving creates the first stored version'}
              </p>
            </div>
            {baseConfig && (
              <button
                onClick={() => openHistory(baseConfig)}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 flex items-center gap-1.5"
              >
                <History className="w-3.5 h-3.5" /> History ({baseConfig.versionsCount})
              </button>
            )}
          </div>

          <textarea
            value={baseContent}
            onChange={(e) => setBaseContent(e.target.value)}
            rows={18}
            spellCheck={false}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-xs font-mono focus:border-[#1e73be] outline-none resize-y leading-relaxed"
          />
          <p className="text-[11px] text-gray-400 mt-1">
            The JSON output format is enforced by the system and appended automatically — this
            prompt defines only the review criteria and style.
          </p>

          <div className="flex items-center gap-3 mt-3">
            <input
              type="text"
              value={baseNotes}
              onChange={(e) => setBaseNotes(e.target.value)}
              placeholder="Change notes (shown in version history)"
              className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none"
            />
            <button
              onClick={saveBase}
              disabled={isSaving || !baseContent.trim()}
              className="px-5 py-2 rounded-xl bg-[#1e73be] hover:bg-[#1e73be]/90 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save new version
            </button>
          </div>
        </div>

        {/* Overrides */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-gray-700">Overrides</h3>
            <button
              onClick={addOverride}
              className="px-3 py-1.5 rounded-lg bg-[#1e73be] text-white text-xs font-semibold flex items-center gap-1.5 hover:bg-[#1e73be]/90"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
          <p className="text-xs text-gray-400 mb-3">
            Inheritance: OE override → question-type override → base prompt.
          </p>

          {overrides.length === 0 ? (
            <p className="text-sm text-gray-400 italic">
              No overrides — all questions use the base prompt.
            </p>
          ) : (
            <div className="space-y-2">
              {overrides.map((config) => (
                <div key={config._id} className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">
                        {config.level === 'oe' ? `OE: ${config.oeCode}` : config.questionType}
                      </p>
                      <p className="text-[11px] text-gray-400">
                        v{config.activeVersion} · {config.versionsCount} version(s)
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => editOverride(config)}
                        className="p-1.5 rounded-lg text-gray-500 hover:text-[#1e73be] hover:bg-blue-50"
                        title="Edit (creates a new version)"
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openHistory(config)}
                        className="p-1.5 rounded-lg text-gray-500 hover:text-[#1e73be] hover:bg-blue-50"
                        title="Version history"
                      >
                        <History className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => removeOverride(config)}
                        className="p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50"
                        title="Remove override"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Version history modal */}
      {historyFor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">
                Version history —{' '}
                {historyFor.level === 'stage'
                  ? SUBAGENT_LABELS[historyFor.subagent]
                  : historyFor.level === 'oe'
                    ? `OE ${historyFor.oeCode}`
                    : historyFor.questionType}
              </h2>
              <button
                onClick={() => setHistoryFor(null)}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              {versions.map((v) => (
                <div
                  key={v._id}
                  className={`rounded-xl border p-4 ${
                    v.version === historyFor.activeVersion
                      ? 'border-[#1e73be] bg-blue-50/40'
                      : 'border-gray-100 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-sm font-bold text-gray-800">v{v.version}</span>
                      {v.version === historyFor.activeVersion && (
                        <span className="ml-2 px-2 py-0.5 rounded bg-[#1e73be] text-white text-[10px] font-bold">
                          ACTIVE
                        </span>
                      )}
                      <p className="text-xs text-gray-500 mt-0.5">
                        {v.createdByName} · {new Date(v.createdAt).toLocaleString()}
                        {v.notes ? ` — ${v.notes}` : ''}
                      </p>
                    </div>
                    {v.version !== historyFor.activeVersion && (
                      <button
                        onClick={() => rollback(historyFor, v.version)}
                        className="px-3 py-1.5 rounded-lg border border-[#1e73be] text-[#1e73be] text-xs font-semibold hover:bg-blue-50 flex items-center gap-1.5"
                      >
                        <RotateCcw className="w-3.5 h-3.5" /> Rollback to this
                      </button>
                    )}
                  </div>
                  <pre className="text-[11px] text-gray-600 whitespace-pre-wrap font-mono bg-white rounded-lg border border-gray-100 p-3 max-h-40 overflow-y-auto">
                    {v.content}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PromptManagement;
